import type { GuestSection } from '@whoworks/shared';
import type { AssistantAction, Role, SearchPeriod, SearchType } from '@whoworks/shared';

export type StatusMode = 'available' | 'busy' | 'away' | 'focus' | 'offline';

export type AuthUser = {
  id: number;
  name: string;
  role: Role;
  active: boolean;
  mustChangePassword?: boolean;
};

export type RosterUser = {
  id: number;
  name: string;
  role: Role;
  active: boolean;
  bio: string | null;
  statusText: string | null;
  statusMode: StatusMode;
  // Hex colour the person picked for this status; null = use the mode's colour.
  statusColor: string | null;
  avatarUrl: string | null;
  coverUrl: string | null;
  lastSeenAt: string | null;
  online: boolean;
  weekSeconds: number;
  streakDays: number;
  githubLogin?: string | null;
};

export type ProjectMember = {
  userId: number;
  role: string;
  name: string;
  avatarUrl: string | null;
  statusMode: StatusMode;
  // Hex colour the person picked for this status; null = use the mode's colour.
  statusColor: string | null;
  online: boolean;
};

export type ProjectInvitation = {
  id: number;
  projectId: number;
  email: string | null;
  invitedName: string | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdByName: string | null;
  acceptedUserId: number | null;
  acceptedUserName: string | null;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
};

export type PublicInvitation = {
  projectName: string;
  projectColor: string;
  inviterName: string | null;
  invitedName: string | null;
  expiresAt: string;
};

export type LoginUser = {
  id: number;
  name: string;
  avatarUrl: string | null;
};

export type ProjectWorker = {
  sessionId: number;
  userId: number;
  userName: string;
  startedAt: string;
  // Presence of the worker: when offline, timers render paused at lastSeenAt.
  online: boolean;
  lastSeenAt: string | null;
  /** Na ktorej podkategórii sa robí. null = na skupine ako celku. */
  subProjectId: number | null;
  subProjectName: string | null;
};

/** Podkategória projektu — samostatný projekt zaradený pod nadradený. */
export type ProjectChild = {
  id: number;
  name: string;
  color: string;
  archived: boolean;
  /** Má prepojené GitHub repo. Chýba v starších odpovediach (cache z úložiska). */
  hasRepo?: boolean;
};

export type ProjectListItem = {
  guestPermissions?: GuestSection[];
  id: number;
  name: string;
  description: string | null;
  color: string;
  createdAt: string;
  archived: boolean;
  // Everyone currently working on the project (oldest session first).
  workers: ProjectWorker[];
  // Back-compat alias: workers[0] ?? null.
  currentWorker: ProjectWorker | null;
  lastLog: {
    endedAt: string;
    note: string;
    userName: string;
  } | null;
  unreadMessages: number;
  /**
   * Skupina má aspoň jeden testovací scenár. Podľa toho sa v detaile ukáže
   * záložka Testy. Voliteľné: zoznam z úložiska (offline štart) je spred
   * pridania poľa a chýbajúca hodnota nesmie záložku navždy skryť.
   */
  hasTests?: boolean;
  /** Má prepojené GitHub repo — iba vtedy má záložka Git čo ukázať. */
  hasRepo?: boolean;
  /** Podkategórie. Prázdne pole = obyčajný projekt bez rozdelenia. */
  children: ProjectChild[];
};

export type WatchItem = {
  id: number;
  kind: 'project' | 'user';
  targetId: number;
  createdAt: string;
};

export type ProjectDocument = {
  id: number;
  projectId: number;
  userId: number;
  userName: string;
  name: string;
  mime: string | null;
  size: number;
  createdAt: string;
};

// Owner/admin storage overview for the Diagnostika section.
export type UsageInfo = {
  db: {
    provider?: string;
    bytes: number | null;
    tables: Array<{ name: string; rows: number }>;
    limitBytes: number;
  };
  r2: {
    objects: number;
    bytes: number;
    limitBytes: number;
  };
};

export type ProjectCommit = {
  id: number;
  sha: string;
  message: string;
  url: string | null;
  branch: string | null;
  committedAt: string | null;
  authorLogin: string | null;
  authorName: string | null;
  userId: number | null;
  userName: string | null;
  userAvatarUrl: string | null;
};

export type UserCommit = {
  id: number;
  sha: string;
  message: string;
  url: string | null;
  branch: string | null;
  committedAt: string | null;
  projectId: number | null;
  projectName: string | null;
};

export type GitBranchStat = {
  branch: string;
  count: number;
  lastCommittedAt: string | null;
};

export type GitContributor = {
  userId: number | null;
  userName: string | null;
  userAvatarUrl: string | null;
  authorName: string | null;
  authorLogin: string | null;
  count: number;
  lastCommittedAt: string | null;
};

export type GitOverview = {
  repoFullName: string | null;
  totals: {
    commitCount: number;
    branchCount: number;
    contributorCount: number;
    firstCommitAt: string | null;
    lastCommitAt: string | null;
  };
  branches: GitBranchStat[];
  contributors: GitContributor[];
};

export type CommitsCursor = { cursorTime: string; cursorId: number };

// Feed rows carry sortAt (committed_at or, when null, ingest time) so the client
// can day-group and the server can keyset-paginate on the same key.
export type CommitFeedItem = ProjectCommit & { sortAt: string };

export type CommitsPage = {
  items: CommitFeedItem[];
  nextCursor: CommitsCursor | null;
};

export type MessageReaction = {
  emoji: string;
  count: number;
  mine: boolean;
};

// Image attached to a message — binary is served via
// GET /api/messages/:id/attachment (channel access enforced server-side).
export type MessageAttachment = {
  name: string;
  mime: string;
  size: number;
};

export type ChatPollVoter = { userId: number; userName: string; avatarUrl: string | null };
export type ChatPollOption = { id: number; text: string; count: number; voters: ChatPollVoter[] };
export type ChatPoll = {
  id: number;
  question: string;
  allowMultiple: boolean;
  closedAt: string | null;
  options: ChatPollOption[];
  myVotes: number[];
};

export type ChatMessage = {
  id: number;
  projectId: number | null;
  userId: number;
  userName: string;
  body: string;
  createdAt: string;
  /** Kedy bola správa opravená. `null`, kým sa jej nikto nedotkol. */
  editedAt: string | null;
  replyTo: { id: number; userId: number; userName: string; body: string } | null;
  reactions: MessageReaction[];
  attachment: MessageAttachment | null;
  poll: ChatPoll | null;
};

// Another participant's read marker in a chat channel — drives the
// Messenger-style "seen" avatar indicators.
export type ChannelRead = {
  userId: number;
  userName: string;
  avatarUrl: string | null;
  lastReadMessageId: number | null;
};

export type FeedItem = {
  id: number;
  type: string;
  actorUserId: number | null;
  actorName: string | null;
  entityType: string;
  entityId: string;
  payload: Record<string, unknown>;
  createdAt: string;
  projectId: number | null;
  projectName: string | null;
};

export type AuditEvent = {
  id: number;
  type: string;
  actorUserId: number | null;
  actorName: string | null;
  entityType: string;
  entityId: string;
  payload: Record<string, unknown>;
  createdAt: string;
};

/**
 * Zúženie globálneho hľadania. Nič z toho nie je povinné — bez filtrov sa hľadá
 * všade, ako predtým. Server filtre iba pridáva k tomu, čo človek vidí aj tak.
 */
export type SearchFilters = {
  type?: SearchType;
  projectId?: number;
  period?: SearchPeriod;
};

export type SearchResults = {
  projects: Array<{ id: number; name: string; color: string }>;
  notes: Array<{ id: number; title: string }>;
  tasks: Array<{
    id: number;
    title: string;
    completed: boolean;
    status: TaskStatus;
    ticketNumber: number | null;
    projectId: number | null;
    projectName: string | null;
    userName: string;
  }>;
  tests: Array<{
    id: number;
    title: string;
    projectId: number;
    projectName: string;
    priority: 'P1' | 'P2' | 'P3' | null;
  }>;
  messages: Array<{
    id: number;
    body: string;
    projectId: number | null;
    projectName: string | null;
    recipientUserId: number | null;
    userId: number;
    userName: string;
  }>;
  /**
   * Prílohy podľa názvu súboru. Voliteľné preto, že web a API sa nasadzujú
   * zvlášť — staršie API pole nepošle a hľadanie kvôli tomu nesmie spadnúť.
   */
  attachments?: Array<{
    id: number;
    name: string;
    mime: string | null;
    source: 'task' | 'task_comment' | 'test_comment';
    commentId: number | null;
    taskId: number | null;
    ticketNumber: number | null;
    scenarioId: number | null;
    parentTitle: string;
    projectId: number | null;
    projectName: string | null;
  }>;
  /** Leady (obchodný zoznam). Voliteľné z rovnakého dôvodu ako prílohy. */
  leads?: Array<{
    id: number;
    name: string;
    city: string;
    status: string;
    projectId: number;
    projectName: string | null;
  }>;
};

export type BriefingItem = {
  sessionId: number;
  body: string;
  endedAt: string;
  fromUserName: string;
  fromUserId: number;
};

export type UserProfile = {
  user: RosterUser;
  streakDays: number;
  weekSeconds: number;
  lastWeekSeconds: number;
  liveSession: { id: number; projectId: number; startedAt: string; projectName: string } | null;
  recentSessions: Array<{
    id: number;
    projectId: number;
    projectName: string;
    startedAt: string;
    endedAt: string;
    durationSeconds: number;
    endNote: string | null;
    tag: string | null;
    tags?: string[];
  }>;
  days: Array<{ day: string; seconds: number }>;
  tags: Array<{ tag: string; seconds: number }>;
  calendar: Array<{ day: string; seconds: number }>;
  totals: { totalSeconds: number; totalCommits: number; bestMonthSeconds: number };
  projectTime: Array<{ id: number; name: string; color: string; seconds: number }>;
};

export type WeeklyRecap = {
  body: string | null;
  generatedAt: string | null;
  error?: string;
  source?: 'ai' | 'fallback';
  model?: string | null;
  metrics: {
    hours: number;
    people: number;
    commits: number;
    ticketsDone: number;
    topPeople: Array<{ name: string; hours: number }>;
    topProjects: Array<{ name: string; hours: number }>;
  };
};

export type AssistantResponse = {
  answer: string;
  // Set when the server understood the message as an order. It is a proposal:
  // nothing runs until the user confirms the card in the assistant.
  action: AssistantAction | null;
  generatedAt: string;
  source: 'ai' | 'fallback' | 'action';
  model: string | null;
  suggestions: string[];
  snapshot: {
    projects: number;
    liveSessions: number;
    livePeople: number;
    openTasks: number;
    myOpenTasks: number;
    urgentTasks: number;
    todayPlans: number;
    todayMyPlans: number;
    weekHours: number;
  };
};

export type ProjectAiOverview = {
  projectId: number;
  projectName: string;
  body: string;
  generatedAt: string;
  source: 'ai' | 'fallback';
  model: string | null;
  metrics: {
    weekHours: number;
    previousWeekHours: number;
    trendPercent: number;
    weekSessions: number;
    activePeople: number;
    activeWorkers: number;
    openTickets: number;
    inProgressTickets: number;
    testingTickets: number;
    doneThisWeek: number;
    highPriorityOpen: number;
    commitsThisWeek: number;
    testsTotal: number;
    testsPassing: number;
    testsFailing: number;
    testsUntested: number;
  };
};

export type ProjectPulse = {
  projectId: number;
  totalSeconds: number;
  weekSeconds: number;
  sessionCount: number;
  topContributors: Array<{ userId: number; name: string; seconds: number }>;
  openTasks: number;
};

export type SessionItem = {
  id: number;
  projectId: number;
  userId: number;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  endNote: string | null;
  handoffToUserId: number | null;
  tag: string | null;
  tags?: string[];
  userName: string;
  projectName: string;
};

export type PlanItem = {
  id: number;
  projectId: number;
  userId: number;
  startTime: string;
  endTime: string;
  note: string | null;
  createdAt: string;
  userName: string;
  projectName: string;
};

// 'private' is the author alone; 'public' is the team, narrowed to the members
// of the note's project when it has one.
export type NoteVisibility = 'private' | 'public';

export type NoteItem = {
  id: number;
  userId: number;
  title: string;
  content: string | null;
  color: string;
  pinned: boolean;
  archived: boolean;
  visibility: NoteVisibility;
  projectId: number | null;
  createdAt: string;
  updatedAt: string;
  userName: string;
  projectName: string | null;
  projectColor: string | null;
};

export type ExpenseSplitItem = {
  userId: number;
  userName: string;
  shareCents: number;
};

export type ExpenseCategory =
  | 'ai'
  | 'hosting'
  | 'domain'
  | 'marketing'
  | 'software'
  | 'hardware'
  | 'services'
  | 'other';

export type ExpenseItem = {
  id: number;
  projectId: number | null;
  projectName: string | null;
  title: string;
  amountCents: number;
  currency: string;
  paidByUserId: number;
  paidByUserName: string;
  createdByUserId: number;
  createdByUserName: string;
  splitMode: 'equal' | 'custom';
  category: ExpenseCategory | null;
  note: string | null;
  expenseDate: string;
  settled: boolean;
  createdAt: string;
  updatedAt: string;
  splits: ExpenseSplitItem[];
};

export type TaskStatus = 'open' | 'in_progress' | 'testing' | 'done';

export type TodoTaskItem = {
  id: number;
  projectId: number | null;
  ticketNumber: number | null;
  testScenarioId: number | null;
  testScenarioTitle: string | null;
  userId: number;
  title: string;
  note: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  dueAt: string | null;
  status: TaskStatus;
  completed: boolean;
  completedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userName: string;
  createdByUserId: number | null;
  createdByUserName: string | null;
  projectName: string | null;
  labels?: TaskLabel[];
  attachmentCount?: number;
  commentCount?: number;
  // Commits whose message mentioned this ticket's key (T-123).
  commitCount?: number;
  // Expected effort, and the time actually booked onto this ticket by anyone.
  estimateMinutes?: number | null;
  trackedSeconds?: number;
  // Checklist progress, so the card can show 3/5 without a second request.
  checklistTotal?: number;
  checklistDone?: number;
  // Unfinished tickets this one is waiting on — drives the board's blocked mark.
  blockedByOpen?: number;
  // Sprint the ticket belongs to, if any.
  sprintId?: number | null;
};

// One entry of a ticket's change log, read back out of the events table.
export type TaskHistoryItem = {
  id: number;
  type: string;
  actorUserId: number | null;
  actorName: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
};

// A template plus a cadence: every everyDays days a fresh ticket appears.
export type TaskRecurrence = {
  id: number;
  projectId: number;
  userId: number;
  userName: string;
  title: string;
  note: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  everyDays: number;
  nextRunAt: string;
  lastCreatedAt: string | null;
  active: boolean;
};

export type WipLimit = { status: TaskStatus; maxCount: number };

export type Sprint = {
  id: number;
  projectId: number;
  name: string;
  startsAt: string;
  endsAt: string;
  closedAt: string | null;
  total: number;
  done: number;
};

export type SprintBurndown = {
  sprint: { id: number; name: string; startsAt: string; endsAt: string };
  total: number;
  done: number;
  // remaining === -1 marks a day in the future, which has nothing to measure yet.
  points: Array<{ day: string; remaining: number; ideal: number }>;
};

export type TaskLink = {
  id: number;
  ticketNumber: number | null;
  title: string;
  status: TaskStatus;
  projectId: number | null;
};

export type ChecklistItem = {
  id: number;
  taskId: number;
  title: string;
  done: boolean;
  position: number;
  doneByUserName: string | null;
  doneAt: string | null;
};

export type TaskTimeEntry = {
  id: number;
  userId: number;
  userName: string;
  userAvatarUrl: string | null;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  endNote: string | null;
  tag: string | null;
};

/** Odpracované na tickete jedným človekom, zo všetkých jeho sedení (T-26). */
export type TaskTimePerson = {
  userId: number;
  userName: string;
  userAvatarUrl: string | null;
  /** Iba dokončené sedenia. */
  seconds: number;
  sessions: number;
  /** Začiatok sedenia, ktoré na tickete práve beží, inak null. */
  runningSince: string | null;
};

export type TaskCommit = {
  id: number;
  sha: string;
  message: string;
  url: string | null;
  branch: string | null;
  committedAt: string | null;
  authorName: string | null;
  authorLogin: string | null;
  userId: number | null;
  userName: string | null;
  userAvatarUrl: string | null;
};

export type TaskLabel = {
  id: number;
  name: string;
  color: string;
};

export type ProjectLabel = {
  id: number;
  projectId: number;
  name: string;
  color: string;
  createdAt: string;
};

export type TaskAttachment = {
  id: number;
  taskId: number;
  userId: number;
  userName: string;
  name: string;
  mime: string | null;
  size: number;
  createdAt: string;
};

// A test run ("beh") groups one pass through the suite. Run id 0 is the
// ad-hoc bucket for testing outside any run — see migration 0036.
export const AD_HOC_TEST_RUN_ID = 0;

export type TestRun = {
  id: number;
  projectId: number;
  name: string;
  createdByUserId: number | null;
  createdByUserName: string | null;
  createdAt: string;
  closedAt: string | null;
  // Set once a public read-only report link exists for this run.
  sharedAt: string | null;
  passed: number;
  failed: number;
  total: number;
};

// Public run report (/r/:token) — no auth, deliberately no tester names.
export type PublicRunReport = {
  run: { name: string; projectName: string; createdAt: string; closedAt: string | null };
  summary: {
    total: number;
    passed: number;
    failed: number;
    partial: number;
    blocked: number;
    obsolete: number;
    untested: number;
  };
  scenarios: Array<{
    id: number;
    title: string;
    section: string | null;
    priority: 'P1' | 'P2' | 'P3' | null;
    status: TestResultStatus | 'untested';
    failNote: string | null;
  }>;
};

// Porovnanie dvoch behov (T-39) — čo sa pokazilo a čo opravilo.
export type TestRunOutcome = TestResultStatus | 'untested';
export type TestRunChangeKind =
  | 'regression'
  | 'fixed'
  | 'newly_tested'
  | 'not_retested'
  | 'unchanged';

export type TestRunComparison = {
  base: { id: number; name: string };
  head: { id: number; name: string };
  total: number;
  summary: Record<TestRunChangeKind, number>;
  changes: Array<{
    id: number;
    title: string;
    section: string | null;
    priority: 'P1' | 'P2' | 'P3' | null;
    before: TestRunOutcome;
    after: TestRunOutcome;
    kind: TestRunChangeKind;
  }>;
};

export type TestRunStats = {
  passed: number;
  failed: number;
  total: number;
};

// pass/fail was not enough for real QA — see migration 0042.
export type TestResultStatus = 'pass' | 'partial' | 'fail' | 'blocked' | 'obsolete';

export type TestResult = {
  userId: number;
  userName: string;
  status: TestResultStatus;
  note: string | null;
  /** Only for 'blocked': the scenario that has to be fixed first. */
  blockedByScenarioId: number | null;
  blockedByTitle: string | null;
  createdAt: string;
};

export type TestScenario = {
  id: number;
  projectId: number;
  title: string;
  steps: string[];
  priority: 'P1' | 'P2' | 'P3' | null;
  section: string | null;
  archivedAt: string | null;
  createdByUserId: number | null;
  createdByUserName: string | null;
  assigneeUserId: number | null;
  assigneeUserName: string | null;
  createdAt: string;
  /** Posledná zmena názvu alebo krokov — priradenie ani archív ho neposúvajú. */
  updatedAt: string | null;
  /** Kto tú zmenu spravil, keď sa to dá dohľadať (T-41). */
  editedByUserName?: string | null;
  results: TestResult[];
  commentCount: number;
  tickets: Array<{
    id: number;
    ticketNumber: number;
    title: string;
    status: TaskStatus;
  }>;
};

// ---- Ideas: the company-wide proposal board ---------------------------------
export type IdeaVoter = { userId: number; userName: string; avatarUrl: string | null };

export type Idea = {
  id: number;
  title: string;
  body: string | null;
  projectId: number | null;
  projectName: string | null;
  projectColor: string | null;
  userId: number;
  userName: string;
  status: 'open' | 'rejected' | 'converted';
  taskId: number | null;
  ticketNumber: number | null;
  upVotes: number;
  downVotes: number;
  score: number;
  /** Counted from the votes, never stored — three in favour is the team's yes. */
  backed: boolean;
  myVote: -1 | 0 | 1;
  voters: { up: IdeaVoter[]; down: IdeaVoter[] };
  commentCount: number;
  aiPrompt: string | null;
  aiPromptAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type IdeaComment = {
  id: number;
  ideaId: number;
  userId: number;
  userName: string;
  body: string | null;
  attachmentName: string | null;
  attachmentMime: string | null;
  attachmentSize: number | null;
  attachments: CommentAttachment[];
  hasAttachment: boolean;
  createdAt: string;
};

export type CommentAttachment = {
  id: number;
  name: string;
  mime: string;
  size: number;
};

export type TaskComment = {
  id: number;
  taskId: number;
  userId: number;
  userName: string;
  body: string | null;
  // Štyri polia nižšie sú prvá príloha zapísaná priamo na komentári. Server ich
  // píše ďalej (0060), appka číta `attachments` — na starých komentároch je tam
  // presne tá istá jedna príloha.
  attachmentName: string | null;
  attachmentMime: string | null;
  attachmentSize: number | null;
  attachments: CommentAttachment[];
  hasAttachment: boolean;
  createdAt: string;
};

export type TestComment = {
  id: number;
  scenarioId: number;
  userId: number;
  userName: string;
  body: string | null;
  // Ako pri TaskComment: štyri staré polia sú prvá príloha, `attachments` je
  // celý zoznam (0060).
  attachmentName: string | null;
  attachmentMime: string | null;
  attachmentSize: number | null;
  attachments: CommentAttachment[];
  hasAttachment: boolean;
  createdAt: string;
};

export type PushPreferences = {
  handoffRequest: boolean;
  handoffResponse: boolean;
  quickHandoffReady: boolean;
  sessionReminder: boolean;
  dailySummary: boolean;
  authLogin: boolean;
  teamActivityStarted: boolean;
  teamActivityFinished: boolean;
  gitPushEta: boolean;
  todoCompleted: boolean;
  ping: boolean;
  chatMessage: boolean;
  watch: boolean;
  appUpdate: boolean;
};

export type AppRelease = {
  version: string;
  title: string;
  summary: string;
  notes: string[];
  publishedAt: string;
};

// Wall-clock window in which nothing is delivered. fromMinutes > toMinutes means
// the window wraps past midnight, which is the usual 22:00 -> 07:00 case.
/** A status this person saved for themselves, e.g. "Na obede" + away. */
// ---- Produkty: dashboardy cudzích appiek ------------------------------------

export type ProductMode = 'pull' | 'push';
export type ProductProvider = 'supabase' | 'cloudflare_d1';
export type VisitsProvider = 'plausible';
export type ProductVisibility = 'owner' | 'admins' | 'team' | 'custom';

/** 'new' pribudlo za deň · 'total' stav k dňu · 'distinct' koľko rôznych ľudí */
export type ProductMetricKind = 'new' | 'total' | 'distinct';

export type ProductMetric = {
  key: string;
  label: string;
  table: string;
  timeColumn: string;
  kind: ProductMetricKind;
  /** Povinné iba pri 'distinct'. */
  personColumn: string | null;
};

export type ProductMapping = {
  rpcFunction: string | null;
  metrics: ProductMetric[];
  people: {
    table: string;
    timeColumn: string;
    personColumn: string;
    labelColumn: string | null;
    statusColumn: string | null;
  } | null;
  errors: {
    table: string;
    timeColumn: string;
    messageColumn: string;
    kindColumn: string | null;
  } | null;
};

export type ProductItem = {
  id: number;
  name: string;
  color: string;
  mode: ProductMode;
  provider: ProductProvider | null;
  host: string | null;
  /** Identifikátor projektu u poskytovateľa. Neowner ho nedostane. */
  ref: string | null;
  mapping: ProductMapping;
  visibility: ProductVisibility;
  /** Whether a key is stored — the key itself never leaves the server. */
  hasSecret: boolean;
  visitsProvider: VisitsProvider | null;
  visitsRef: string | null;
  hasVisitsSecret: boolean;
  /** Kedy sa o čísla naposledy pokúsil zber — aj keď zlyhal. */
  lastSyncAt: string | null;
  /**
   * Kedy čísla naposledy naozaj prišli. Pri zlyhanom zbere je to jediný čas,
   * ktorý hovorí pravdu; `lastSyncAt` sa prepisuje aj pri zlyhaní.
   *
   * Voliteľné iba kvôli starším odpovediam servera počas nasadenia.
   */
  lastDataAt?: string | null;
  lastError: string | null;
  /**
   * Externý zdroj (dnes Sentry), ktorý nezbiera.
   *
   * Vlastné pole a nie `lastError`, zámerne: produkt môže svoje čísla zbierať
   * úplne v poriadku a mať pritom mŕtvy prítok chýb. Zliať to do jednej vety
   * by znamenalo, že „nezbiera nič" vyzerá rovnako ako „chodí všetko okrem
   * chýb" — a to prvé je o triedu horšie.
   */
  sourceError: string | null;
  archived: boolean;
  createdAt: string;
  /** Toto je samotná ONVI WORKS — meria sama seba. */
  selfSource: boolean;
  /** Ako často sa má produkt ozvať, v minútach. null = nestrážiť. */
  expectEveryMinutes: number | null;
};

/**
 * Kde Sentry organizácia býva.
 *
 * Vyberá sa pri jej založení raz a nedá sa zmeniť. Hostiteľ sa z toho skladá
 * na serveri podľa pevnej tabuľky — nikdy z toho, čo človek napíše, lebo kľúč,
 * ktorý raz odíde na cudziu adresu, je preč.
 */
export type SentryRegion = 'us' | 'de';

/**
 * Nastavený externý zdroj tak, ako ho vracia `/api/products/:id/sources`.
 *
 * Token tu nie je a nikdy nebude — o kľúči sa navonok hovorí jediná vec: či
 * tam nejaký je. Formulár podľa toho vie napísať „token je uložený" namiesto
 * toho, aby prázdne pole vyzeralo ako chýbajúci kľúč.
 */
export type ProductSourceItem = {
  kind: 'sentry';
  region: SentryRegion;
  /** Pri Sentry organizácia. */
  org: string;
  /** Pri Sentry projekt. */
  project: string | null;
  hasSecret: boolean;
  lastSyncAt: string | null;
  /** Prečo zdroj nezbiera. null = zbiera. */
  lastError: string | null;
};

/** Závažnosť podľa zdroja. Voľný `kind` na to nestačil — nedal sa filtrovať. */
export type ProductErrorLevel = 'critical' | 'error' | 'warning' | 'info';

/** Stav sa zo zdroja iba zrkadlí; späť sa nezapisuje. */
export type ProductErrorStatus = 'open' | 'ignored' | 'resolved';

/**
 * Jedna chyba tak, ako ju vracia `/api/products/:id/errors`.
 *
 * Dashboard vracia užší tvar — bez úrovne a stopy — lebo tam je zoznam chýb
 * len prívesok. Kto chce chybu naozaj riešiť, ide cez vlastný endpoint.
 */
export type ProductError = {
  id: number;
  kind: string | null;
  message: string;
  count: number;
  level: ProductErrorLevel | null;
  stack: string | null;
  environment: string | null;
  permalink: string | null;
  status: ProductErrorStatus | null;
  firstSeenAt: string;
  lastSeenAt: string;
  taskId: number | null;
};

export type ProductDashboard = {
  product: ProductItem;
  series: Array<{
    metric: string;
    /** Čitateľný názov metriky. Posiela ho iba cesta push; inak je to null. */
    label: string | null;
    points: Array<{ day: string; value: number }>;
  }>;
  people: Array<{ person: string; label: string | null; metric: string; value: number }>;
  errors: Array<{
    id: number;
    kind: string | null;
    message: string;
    count: number;
    lastSeenAt: string;
    taskId: number | null;
  }>;
  /** Koľko chýb pribudlo v ktorý deň, cez všetky odtlačky spolu. */
  errorDays: Array<{ day: string; count: number }>;
  summary: { body: string; generatedAt: string } | null;
};

export type StatusPreset = {
  id: number;
  label: string;
  mode: StatusMode;
  color: string | null;
};

export type QuietHours = {
  enabled: boolean;
  fromMinutes: number;
  toMinutes: number;
  weekends: boolean;
};

/**
 * Riadok osobnej schránky. Vzniká tam, kde sa posiela upozornenie — je to tá
 * istá udalosť, len taká, ktorá počká (migrácia 0061).
 */
export type InboxItem = {
  id: number;
  type: string | null;
  title: string;
  body: string;
  url: string | null;
  projectId: number | null;
  createdAt: string;
  readAt: string | null;
};

/**
 * Položka „Čaká na mňa“. Na rozdiel od `InboxItem` sa neukladá — server ju
 * vyráta z komentárov, ticketov, scenárov a ankiet (apps/api/src/waiting.ts).
 * `key` je stabilný, `createdAt` je vždy ISO so „Z“.
 */
export type WaitingKind = 'mention' | 'comment' | 'testing' | 'tests' | 'poll' | 'stale' | 'leads';
export type WaitingItem = {
  key: string;
  kind: WaitingKind;
  title: string;
  body: string;
  url: string;
  projectId: number | null;
  projectName: string | null;
  actorName: string | null;
  createdAt: string;
};

/**
 * Najvyššie id z tabuliek, do ktorých sa pridáva. Slúži ako známka zmeny —
 * appka sa pýta na toto (tri prečítané riadky) a drahé dotazy si vypýta až
 * vtedy, keď sa niektoré číslo pohne.
 *
 * Zachytí pribudnutie, nie úpravu ani zmazanie. Preto to pollovanie
 * nenahrádza, iba preriedi.
 */
export type ChangeMarks = {
  events: number;
  messages: number;
  sessions: number;
  tasks: number;
  projects: number;
  notes: number;
  expenses: number;
  inbox: number;
};

/** Kto v kanáli práve píše (chodí s načítaním správ, pozri chat-typing.ts v API). */
export type ChatTyping = { userId: number; at: string };

/** Pull request prepojený s tiketou cez `T-42` v názve vetvy alebo PR. */
export type TaskPullRequest = {
  number: number;
  title: string;
  state: 'open' | 'draft' | 'merged' | 'closed';
  url: string | null;
  branch: string | null;
  baseBranch: string | null;
  repo: string;
  author: string | null;
  updatedAt: string;
};

/** Vetva, ktorá tiketu spomína v názve a zatiaľ k nej nie je PR. */
export type TaskBranch = {
  name: string;
  url: string | null;
  repo: string;
  author: string | null;
  createdAt: string;
};

/** Posledných 7 dní oproti 7 dňom pred nimi a cieľ, ak je nastavený. */
export type ProductWeekMetric = {
  metric: string;
  label: string | null;
  current: number;
  previous: number;
  /** Zmena v percentách. `null`, keď predtým nebolo nič — z nuly sa percentá nerátajú. */
  changePct: number | null;
  /** Cieľ za 7 dní. */
  target: number | null;
  /** 0..n (1 = splnené). `null` bez cieľa. */
  progress: number | null;
};

export type ProductWeekly = {
  /** Prvý a posledný deň (YYYY-MM-DD) aktuálneho okna. */
  from: string;
  to: string;
  previousFrom: string;
  previousTo: string;
  metrics: ProductWeekMetric[];
};
